import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

export interface DataGridColumn<Row> {
  key: string;
  header: string;
  render: (row: Row) => ReactNode;
  /** LTR cell content (numbers, codes, dates) inside an RTL table */
  ltr?: boolean;
  align?: 'start' | 'end' | 'center';
  width?: string;
}

export interface DataGridProps<Row> {
  rows: readonly Row[];
  columns: readonly DataGridColumn<Row>[];
  getRowId: (row: Row) => string;
  /** Enter / double-click on the selected row (11 §3.4) */
  onActivate?: ((row: Row) => void) | undefined;
  selectedId?: string | undefined;
  onSelect?: ((row: Row) => void) | undefined;
  emptyText: string;
  /** query still pending: a spinner instead of "no results" before the first answer */
  loading?: boolean | undefined;
  caption?: string | undefined;
  dense?: boolean;
  rowClassName?: ((row: Row) => string) | undefined;
  /** no outer border/radius: the grid sits inside a Card that already draws one */
  bare?: boolean;
}

/**
 * Keyboard-first table (11 §6.3): ↑/↓ move the selection, Enter activates, Home/End jump.
 * One roving tabindex on the tbody; selection is announced via aria-selected.
 */
export function DataGrid<Row>({
  rows,
  columns,
  getRowId,
  onActivate,
  selectedId,
  onSelect,
  emptyText,
  loading = false,
  caption,
  dense = true,
  rowClassName,
  bare = false,
}: DataGridProps<Row>) {
  const [internal, setInternal] = useState<string | undefined>(selectedId);
  const current = selectedId ?? internal;
  const bodyRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    if (selectedId !== undefined) setInternal(selectedId);
  }, [selectedId]);

  const select = useCallback(
    (row: Row) => {
      setInternal(getRowId(row));
      onSelect?.(row);
    },
    [getRowId, onSelect],
  );

  function onKeyDown(e: KeyboardEvent<HTMLTableSectionElement>) {
    if (!rows.length) return;
    const idx = Math.max(
      0,
      rows.findIndex((r) => getRowId(r) === current),
    );
    const move = (to: number) => {
      const row = rows[Math.min(Math.max(to, 0), rows.length - 1)]!;
      select(row);
      const el = bodyRef.current?.querySelector<HTMLElement>(`[data-row-id="${getRowId(row)}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    };
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(current ? idx + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(idx - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(rows.length - 1);
        break;
      case 'Enter': {
        const row = rows[idx];
        if (row && current) {
          e.preventDefault();
          onActivate?.(row);
        }
        break;
      }
    }
  }

  // one alignment per column for header and cells (end = left in RTL: amounts line up under their header)
  const alignOf = (c: DataGridColumn<Row>) =>
    c.align === 'end' ? 'text-end' : c.align === 'center' ? 'text-center' : 'text-start';
  // 11 §1 P-07: 32px rows dense, 40px comfortable
  const pad = dense ? 'px-3 py-1.5' : 'px-3 py-2.5';
  return (
    <div className={`overflow-auto bg-surface ${bare ? '' : 'rounded-md border border-border'}`}>
      <table
        aria-busy={loading || undefined}
        className="w-full border-collapse text-sm"
        role="grid"
        aria-rowcount={rows.length}
      >
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead className="sticky top-0 z-[1] bg-surface-muted text-start text-xs font-semibold text-text-muted">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                style={c.width ? { width: c.width } : undefined}
                className={`${pad} ${alignOf(c)}`}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody
          ref={bodyRef}
          tabIndex={0}
          onKeyDown={onKeyDown}
          className="outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-6 text-center text-sm text-text-muted"
              >
                {loading ? (
                  <span
                    aria-hidden="true"
                    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-e-transparent motion-reduce:animate-none"
                  />
                ) : (
                  emptyText
                )}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const id = getRowId(row);
              const selected = id === current;
              return (
                <tr
                  key={id}
                  data-row-id={id}
                  aria-selected={selected}
                  onClick={() => select(row)}
                  onDoubleClick={() => onActivate?.(row)}
                  className={[
                    `border-t border-border ${onActivate ? 'cursor-pointer' : 'cursor-default'}`,
                    selected
                      ? 'bg-primary-soft outline outline-1 -outline-offset-1 outline-primary/60'
                      : 'hover:bg-surface-muted/60',
                    rowClassName?.(row) ?? '',
                  ].join(' ')}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={`${pad} ${c.ltr ? 'num' : ''} ${alignOf(c)}`}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
